// ProviderSentimentBar: horizontal stacked bar showing positive/neutral/negative for a provider.

import React from 'react';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import type { ReviewSignal, Provider } from '../lib/api';
import { Link } from 'react-router-dom';

interface ProviderSentimentBarProps {
  provider: Provider;
  signals: ReviewSignal[];
  lang: Lang;
  code: string;
}

export function ProviderSentimentBar({ provider, signals, lang, code }: ProviderSentimentBarProps) {
  // Aggregate sentiment across all sources
  let totalMentions = 0;
  let totalPositive = 0;
  let totalNeutral = 0;
  let totalNegative = 0;

  for (const s of signals) {
    totalMentions += s.mention_count;
    if (s.mention_count > 0) {
      totalPositive += (s.sentiment_positive ?? 0) * s.mention_count;
      totalNeutral += (s.sentiment_neutral ?? 0) * s.mention_count;
      totalNegative += (s.sentiment_negative ?? 0) * s.mention_count;
    }
  }

  const posRatio = totalMentions > 0 ? totalPositive / totalMentions : 0;
  const neuRatio = totalMentions > 0 ? totalNeutral / totalMentions : 0;
  const negRatio = totalMentions > 0 ? totalNegative / totalMentions : 0;

  const categoryBadge: Record<string, string> = {
    consumer_esim: 'text-sky-600',
    corporate_iot: 'text-emerald-600',
    airline_wifi: 'text-violet-600',
  };

  const detailLabel = lang === 'tr' ? 'sağlayıcı detayı ve kaynak yorumları' : 'open provider detail and source quotes';

  // The whole card is a single link — every visible signal (provider name,
  // mention count, sentiment bar) drills into the provider detail page where
  // the actual source quotes live. Core product-team ask: clicking any data point
  // must reveal the underlying source.
  return (
    <Link
      to={`/providers/${provider.id}?code=${code}`}
      title={detailLabel}
      className="card p-3 block hover:border-emerald-700/40 hover:bg-neutral-100/40 transition-colors group"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-sm font-medium text-neutral-900 group-hover:text-emerald-600 transition-colors truncate">
          {provider.name}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs ${categoryBadge[provider.category] ?? 'text-neutral-500'}`}>
            {t(provider.category, lang)}
          </span>
          <span className="text-xs text-neutral-500 tabular-nums group-hover:text-emerald-600 transition-colors">
            {totalMentions}
            {totalMentions > 0 && (
              <span className="text-neutral-300 group-hover:text-emerald-600/70 ml-0.5">→</span>
            )}
          </span>
        </div>
      </div>

      {/* Stacked sentiment bar */}
      <div className="h-1.5 rounded-full overflow-hidden bg-neutral-200 flex">
        {totalMentions > 0 ? (
          <>
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${posRatio * 100}%` }}
              title={`${t('positive', lang)}: ${Math.round(posRatio * 100)}%`}
            />
            <div
              className="h-full bg-neutral-500 transition-all"
              style={{ width: `${neuRatio * 100}%` }}
              title={`${t('neutral', lang)}: ${Math.round(neuRatio * 100)}%`}
            />
            <div
              className="h-full bg-rose-500 transition-all"
              style={{ width: `${negRatio * 100}%` }}
              title={`${t('negative', lang)}: ${Math.round(negRatio * 100)}%`}
            />
          </>
        ) : (
          <div className="h-full w-full bg-neutral-200" title={t('noData', lang)} />
        )}
      </div>

      {/* Labels */}
      {totalMentions > 0 && (
        <div className="flex justify-between mt-1 text-xs text-neutral-500">
          <span className="text-emerald-600/70">{Math.round(posRatio * 100)}%</span>
          <span>{Math.round(neuRatio * 100)}%</span>
          <span className="text-rose-600/70">{Math.round(negRatio * 100)}%</span>
        </div>
      )}
    </Link>
  );
}
